import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Copy, Check } from "lucide-react";
import logo from "../../../assets/layout/logo.webp";
import "./footer.css";

const services = [
  {
    label: "AI Data Operations & Human-in-the-Loop AI Services",
    href: "/ai-infrastructure/ai-data-operations",
  },
  {
    label: "Sports Annotation & Analytics",
    href: "https://www.qualitasglobalsports.com/",
    external: true,
  },
  {
    label: "Generative AI",
    href: "/ai-infrastructure/generative-ai",
  },
  {
    label: "AI Data Collection",
    href: "/ai-infrastructure/data-collection",
  },
  {
    label: "Global Capability Services",
    href: "/global-capability",
  },
];

const company = [
  { label: "About Us", href: "/about-us" },
  { label: "Careers", href: "/careers" },
  { label: "Blogs & Media", href: "/resources/blogs" },
  { label: "Contact Us", href: "/contact-us" },
];

const LinkedInIcon = ({ size = 17 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2ZM8.34 18H5.67V9.5h2.67ZM7 8.34a1.55 1.55 0 1 1 0-3.1 1.55 1.55 0 0 1 0 3.1ZM18.34 18h-2.67v-4.14c0-.99-.02-2.26-1.38-2.26-1.38 0-1.59 1.08-1.59 2.19V18h-2.67V9.5h2.56v1.16h.04a2.81 2.81 0 0 1 2.53-1.39c2.7 0 3.2 1.78 3.2 4.1Z" />
  </svg>
);

const YouTubeIcon = ({ size = 17 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8ZM9.6 15.6V8.4l6.3 3.6-6.3 3.6Z" />
  </svg>
);

const InstagramIcon = ({ size = 17 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r=".7" fill="currentColor" />
  </svg>
);

const socialLinks = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/qualitasglobal/",
    Icon: LinkedInIcon,
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@qualitasglobalservicesllp2882",
    Icon: YouTubeIcon,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/qualitasglobalservices/?hl=en",
    Icon: InstagramIcon,
  },
];

const offices = [
  {
    country: "India",
    address:
      "Cerebrum IT Park, B-3, 3rd Floor, Office 1A–1C, Kalyani Nagar, Pune, 411014.",
  },
  {
    country: "Netherlands",
    address:
      "Qualitas Global Services B.V., Europalaan 400-1, 3526 KS, Utrecht.",
  },
  {
    country: "USA",
    address: "8 The Green, Suite R, Dover, Delaware, 19901.",
  },
  {
    country: "Germany",
    address: "Representative Office, Carl-Benz-Straße 21, 60386 Frankfurt.",
  },
];

function FooterLink({ href, children, external = false, className = "" }) {
  const classes = `footer-link ${className}`;

  if (external) {
    return (
      <a
        href={href}
        className={classes}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={classes}>
      {children}
    </Link>
  );
}

function Footer() {
  const [copied, setCopied] = useState(false);

  const copyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText("info@qualitasglobal.com");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      window.location.href = "mailto:info@qualitasglobal.com";
    }
  }, []);

  return (
    <footer className="site-footer">
      <div className="footer-main">
        <div className="footer-brand">
          <Link
            to="/"
            className="footer-logo"
            aria-label="Qualitas Global home"
          >
            <img src={logo} alt="Qualitas Global" />
          </Link>

          <p className="footer-description">
            Building better AI with trusted data, human expertise and scalable
            technology solutions.
          </p>

          <div className="footer-contact">
            <div className="footer-email">
              <a href="mailto:info@qualitasglobal.com?subject=Website%20enquiry">
                info@qualitasglobal.com
                <ArrowUpRight size={13} aria-hidden="true" />
              </a>
              <button
                type="button"
                className="footer-copy-email"
                onClick={copyEmail}
                aria-label={copied ? "Email copied" : "Copy email address"}
                title={copied ? "Copied" : "Copy email"}
              >
                {copied ? (
                  <Check size={13} aria-hidden="true" />
                ) : (
                  <Copy size={13} aria-hidden="true" />
                )}
              </button>
            </div>
          </div>

          <div className="footer-socials" aria-label="Social media">
            {socialLinks.map(({ label, href, Icon }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                title={label}
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social-link"
              >
                <Icon size={17} />
              </a>
            ))}
          </div>
        </div>

        <div className="footer-column footer-services">
          <h2 className="footer-heading">
            <span className="footer-heading-mark">/</span>
            Services
          </h2>
          <nav className="footer-links" aria-label="Services">
           {services.map((service) => (
  <FooterLink
    key={service.label}
    href={service.href}
    external={service.external}
  >
    {service.label}
  </FooterLink>
))}
            <FooterLink
              href="https://www.qualitasglobalsports.com/"
              external
              className="footer-sports-link"
            >
              Qualitas Global Sports
              <ArrowUpRight size={15} aria-hidden="true" />
            </FooterLink>
          </nav>
        </div>

        <div className="footer-column">
          <h2 className="footer-heading">
            <span className="footer-heading-mark">/</span>
            Domain Expertise
          </h2>
          <nav className="footer-links" aria-label="Domain expertise">
            <FooterLink href="/industries">Explore Our Industries</FooterLink>
          </nav>
        </div>

        <div className="footer-column">
          <h2 className="footer-heading">
            <span className="footer-heading-mark">/</span>
            Company
          </h2>
          <nav className="footer-links" aria-label="Company">
            {company.map((item) => (
              <FooterLink key={item.label} href={item.href}>
                {item.label}
              </FooterLink>
            ))}
          </nav>
        </div>
      </div>

      <div className="footer-offices-section">
        <div className="footer-offices">
          {offices.map((office) => (
            <address className="footer-office" key={office.country}>
              <span className="footer-office-country">{office.country}</span>
              <span className="footer-office-address">{office.address}</span>
            </address>
          ))}
        </div>
      </div>

      <div className="footer-bottom">
        <p className="footer-copyright">
          © 2026 QUALITAS GLOBAL SERVICES LLP. All rights reserved.
        </p>
        <nav className="footer-legal" aria-label="Legal">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/website-terms">Website Terms of Use</Link>
        </nav>
      </div>
    </footer>
  );
}

export default Footer;
